import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import styles from '../../_css/Project.module.css';
import SectionDivider from '../Simulation/SectionDivider';
import FormInput from '../Simulation/FormInput';
import FormTextArea from '../Simulation/FormTextArea';
import SearchableDrp from '../common/SearchableDrp';
import { countryOptionsWith } from '../Simulation/constants';
import { notifyError } from '../../utils/helper';

function stringFromMaybeNested(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object' && value !== null && value.name != null) return String(value.name);
  return String(value);
}

const EditProjectModal = ({ project, onClose, onSave}) => {
  const [form, setForm] = useState({
    projectName: project?.name ?? '',
    country: stringFromMaybeNested(project?.country ?? project?.market),
    description: project?.simulation_description ?? '',
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (project) {
      setForm({
        projectName: project.name ?? '',
        country: stringFromMaybeNested(project.country ?? project.market),
        description: project.simulation_description ?? '',
      });
    }
  }, [project]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleSave = async(e) => {
    e?.preventDefault();
    if(!form.projectName?.trim()){
      notifyError('Please enter a project name');
      return;
    }
    if(!form.country?.trim()){
      notifyError('Please select a country');
      return;
    }
 
    setLoading(true);
    await onSave?.({
      id: project?.id,
      name: form.projectName.trim(),
      country: form.country.trim(),
      description: form.description?.trim() ?? '',
    });
    setLoading(false);


    onClose?.();
  };

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Edit Project</h2>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeBtn}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.formBody}>
          <SectionDivider label="Project Details" variant="light" />
          <FormInput
            label="Project Name"
            placeholder="e.g., E.ON Sustainable Energy"
            icon="📋"
            required
            variant="light"
            value={form.projectName}
            onChange={(v) => update('projectName', v)}
          />
          <FormTextArea
            label="Project Description"
            placeholder="Describe the project scope and goals..."
            variant="light"
            value={form.description}
            onChange={(v) => update('description', v)}
          />

          <div className={styles.fieldSpacer}>
            <label className={styles.lightFormLabel}>
              Country / Market <span className={styles.requiredAsterisk}>*</span>
            </label>
            <SearchableDrp
              options={countryOptionsWith(form.country)}
              value={form.country}
              onChange={(v) => update('country', v)}
              placeholder="Select a country..."
              icon="🌍"
            />
          </div>

          <div className={styles.footer}>
            <button
              type="button"
              onClick={handleSave}
              className={styles.btnSave}
              disabled={loading}
              style={{ opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Updating Project...' : '💾 Save Changes'}
            </button>
            <button type="button" onClick={onClose} className={styles.btnCancel} disabled={loading} style={{ opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProjectModal;
